// PUN-5144: pure mappers that turn a reviewed crawl's `venue_data` into venue_pages / venues
// writes. No DB access here — the orchestrator (server) injects the slug-existence check and the
// confirmed coordinates, and calls the data layer. Kept pure so the merge/shape rules are unit-tested.

import type { TablesUpdate } from "@punchup/data/supabase.types";
import type { VenueInsert } from "@punchup/data/legacy/venues";

// The social platform keys, matching SocialType in ./venue_pages 1:1. Declared locally (not imported
// as the runtime enum) so this stays a pure, dependency-light module — the crawl's `social{}` keys
// line up with these exactly.
const SOCIAL_KEYS = [
  "website",
  "instagram",
  "facebook",
  "youtube",
  "tiktok",
  "twitter",
  "reddit",
  "discord",
] as const;

// ── the crawler's VenueData shape (mirrors punchup_agent models.py, frozen contract) ──────────────
export type CrawlVenueAddress = {
  street?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
};

export type CrawlVenueHours = {
  monday?: string | null;
  tuesday?: string | null;
  wednesday?: string | null;
  thursday?: string | null;
  friday?: string | null;
  saturday?: string | null;
  sunday?: string | null;
};

export type CrawlVenueSocial = Partial<
  Record<(typeof SOCIAL_KEYS)[number], string | null>
>;

export type CrawlVenueData = {
  name?: string | null;
  city_state?: string | null;
  address?: CrawlVenueAddress | null;
  description?: string | null;
  phone?: string | null;
  email?: string | null;
  hours?: CrawlVenueHours | null;
  logo_img?: string | null;
  social?: CrawlVenueSocial | null;
  // Geocode (PUN-5144). Produced by the crawler agent — lifted from the page when it publishes
  // coordinates, else geocoded from the address agent-side — so it rides the versioned venue_data like
  // the rest of the snapshot. An operator edit can re-pick it via the address autocomplete. Absent when
  // the agent couldn't resolve a location (the UI shows "no location" and the operator can set one).
  latitude?: number | null;
  longitude?: number | null;
};

export type Coords = { latitude: number; longitude: number };

// A trimmed non-empty string, or undefined — the "found" predicate. An absent/blank crawl field is
// never written (so it can't blank out a curated value on the update path).
function found(value: string | null | undefined): string | undefined {
  const trimmed = (value ?? "").trim();
  return trimmed === "" ? undefined : trimmed;
}

// Crawl hours use lowercase full-day keys; the public renderer (VenueHoursDisplay) reads CAPITALIZED
// `Monday..Sunday`. Re-key, keeping only days the crawl actually found, values passed through.
const DAY_KEYS: ReadonlyArray<[keyof CrawlVenueHours, string]> = [
  ["monday", "Monday"],
  ["tuesday", "Tuesday"],
  ["wednesday", "Wednesday"],
  ["thursday", "Thursday"],
  ["friday", "Friday"],
  ["saturday", "Saturday"],
  ["sunday", "Sunday"],
];

// Per-key merge for a jsonb string-map column (venue hours, social links). The existing curated map
// is preserved; only the keys the crawl FOUND are overlaid, so a scrape that found just one key never
// wipes the rest. Crawl found nothing → undefined, so the caller leaves the column untouched (never
// blanks curated data). `hours` and `social` differ only in how they derive their found-keys map.
function mergeFoundKeys(
  existing: Record<string, string>,
  foundKeys: Record<string, string>
): Record<string, string> | undefined {
  if (Object.keys(foundKeys).length === 0) return undefined;
  const base: Record<string, string> = {};
  for (const [key, value] of Object.entries(existing)) {
    const kept = found(value);
    if (kept) base[key] = kept;
  }
  return { ...base, ...foundKeys };
}

// Found days, re-keyed lowercase → the renderer's capitalized `Monday..Sunday`.
function mergeHours(
  existing: Record<string, string>,
  hours: CrawlVenueHours | null | undefined
): Record<string, string> | undefined {
  const foundDays: Record<string, string> = {};
  if (hours) {
    for (const [lower, capitalized] of DAY_KEYS) {
      const value = found(hours[lower]);
      if (value) foundDays[capitalized] = value;
    }
  }
  return mergeFoundKeys(existing, foundDays);
}

// `city_state` is NOT NULL on venue_pages. Prefer the crawl's own; else build it from the address.
function deriveCityState(data: CrawlVenueData): string | undefined {
  const explicit = found(data.city_state);
  if (explicit) return explicit;
  const parts = [found(data.address?.city), found(data.address?.state)].filter(
    (p): p is string => !!p
  );
  return parts.length > 0 ? parts.join(", ") : undefined;
}

/**
 * Pull the typed `venue_data` out of a crawl version's stored `result` jsonb. The single home for the
 * `result.venue_data` unwrap, so routes feed the orchestrator a real `CrawlVenueData` instead of each
 * re-casting the row to an ad-hoc `Record<string, unknown>`.
 */
export function extractCrawlVenueData(result: unknown): CrawlVenueData {
  return (result as { venue_data?: CrawlVenueData } | null)?.venue_data ?? {};
}

/**
 * The venue's map point from a crawl version's `venue_data`, where the crawler agent stores it (lifted
 * from the page, else geocoded from the address agent-side). Both coordinates must be finite and in
 * range; a half-filled or out-of-range pair is treated as "no point", so apply never writes a bogus pin.
 */
export function extractCrawlCoords(data: CrawlVenueData): Coords | null {
  const { latitude, longitude } = data;
  if (typeof latitude !== "number" || typeof longitude !== "number") {
    return null;
  }
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
  if (Math.abs(latitude) > 90 || Math.abs(longitude) > 180) return null;
  return { latitude, longitude };
}

// Found social links, keyed by platform (a subset of SOCIAL_KEYS / SocialType).
function mergeSocials(
  existing: Record<string, string>,
  social: CrawlVenueSocial | null | undefined
): Record<string, string> | undefined {
  const foundSocials: Record<string, string> = {};
  for (const type of SOCIAL_KEYS) {
    const value = found(social?.[type]);
    if (value) foundSocials[type] = value;
  }
  return mergeFoundKeys(existing, foundSocials);
}

/**
 * The venue_pages columns to write from a crawl. Only fields the crawl found are present (never a
 * blank over a curated value). `external_site_info` carries the per-key-merged socials and `hours`
 * the per-key-merged days; pass the page's EXISTING `hours`/`socialLinks` so update preserves keys the
 * crawl didn't find (omit `existing` entirely on create). The two are a single named-key object — not
 * two same-typed positional params — so they can't be silently transposed at a call site.
 */
export function mapVenueDataToVenuePageFields(
  data: CrawlVenueData,
  existing: {
    hours?: Record<string, string>;
    socialLinks?: Record<string, string>;
  } = {}
): Partial<TablesUpdate<"venue_pages">> {
  const fields: Partial<TablesUpdate<"venue_pages">> = {};

  const name = found(data.name);
  if (name) fields.name = name;

  const cityState = deriveCityState(data);
  if (cityState) fields.city_state = cityState;

  const description = found(data.description);
  if (description) fields.description = description;

  const phone = found(data.phone);
  if (phone) fields.phone = phone;

  const email = found(data.email);
  if (email) fields.email = email;

  const logo = found(data.logo_img);
  if (logo) fields.logo_img = logo;

  const hours = mergeHours(existing.hours ?? {}, data.hours);
  if (hours) fields.hours = hours;

  const socials = mergeSocials(existing.socialLinks ?? {}, data.social);
  if (socials) {
    fields.external_site_info = [{ type: "social", links: socials }];
  }

  return fields;
}

/**
 * The columns of a NEW venues row (location) made from a crawl's structured address + the agent's map
 * point; also the crawl's name/postal code the existing-location lookup matches on. An existing
 * location is never written from a crawl. `location_geo` is WKT `POINT(<lng> <lat>)` (longitude
 * first), matching upsertVenue; it is omitted when the crawl has no point.
 */
export function mapVenueDataToVenueFields(
  data: CrawlVenueData,
  coords: Coords | null
): Partial<VenueInsert> {
  const venue: Partial<VenueInsert> = {};

  const name = found(data.name);
  if (name) venue.name = name;

  const street = found(data.address?.street);
  if (street) venue.address = street;

  const city = found(data.address?.city);
  if (city) venue.city = city;

  const state = found(data.address?.state);
  if (state) venue.state = state;

  const postal = found(data.address?.postal_code);
  if (postal) venue.postal_code = postal;

  if (coords) {
    venue.location = { latitude: coords.latitude, longitude: coords.longitude };
    venue.location_geo = `POINT(${coords.longitude} ${coords.latitude})`;
  }

  return venue;
}

// A venue_pages slug is the `/v/{slug}` path segment: lowercase letters and digits in hyphen-joined
// runs (the same rule the venue settings page enforces).
export const VENUE_PAGE_SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

export function isValidVenuePageSlug(slug: string): boolean {
  return VENUE_PAGE_SLUG_PATTERN.test(slug);
}

/** The slug a venue name reads as (may be empty for a name with no letters or digits). */
export function slugifyVenuePageName(name: string): string {
  return (name ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

const SLUG_SUFFIX_ALPHABET = "abcdefghijkmnpqrstuvwxyz23456789";
const SLUG_SUFFIX_ATTEMPTS = 5;

// Four characters from an alphabet without look-alikes (no l/1, o/0), so an operator can read the
// suggested address back without confusion.
function randomSlugSuffix(): string {
  let suffix = "";
  for (let i = 0; i < 4; i += 1) {
    suffix +=
      SLUG_SUFFIX_ALPHABET[
        Math.floor(Math.random() * SLUG_SUFFIX_ALPHABET.length)
      ];
  }
  return suffix;
}

/**
 * A free venue_pages slug for a new page: the slugified name when nobody has it, else the name plus a
 * random suffix (`comedy-mothership-k7qd`) — random rather than `-2`, so the address doesn't read as
 * a second version of the page that holds the plain slug. `slugExists` (wraps getVenuePageBySlug)
 * and `suffix` are injected so this stays pure + testable. Throws when the name has no slug-able
 * characters, or when no free suffix turns up.
 */
export async function generateUniqueVenuePageSlug(
  name: string,
  slugExists: (slug: string) => Promise<boolean>,
  suffix: () => string = randomSlugSuffix
): Promise<string> {
  const base = slugifyVenuePageName(name);
  if (!base) {
    throw new Error("Cannot generate a slug: venue name is required.");
  }
  if (!(await slugExists(base))) return base;
  for (let attempt = 0; attempt < SLUG_SUFFIX_ATTEMPTS; attempt += 1) {
    const candidate = `${base}-${suffix()}`;
    if (!(await slugExists(candidate))) return candidate;
  }
  throw new Error(`Cannot find a free slug for "${base}".`);
}
